import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useTrial } from "../context/TrialContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import GuidedTour from "../components/GuidedTour";
import AIInsightsPanel from "../components/AIInsightsPanel";
import BCMProgramAttention from "../components/BCMProgramAttention";
import BCMHealthDashboard from "../components/BCMHealthDashboard";
import TeamActivityPanel from "../components/TeamActivityPanel";
import PendingApprovalsWidget from "../components/dashboard/PendingApprovalsWidget";
import DemoMaturityBanner from "../components/DemoMaturityBanner";
import RoleBasedInviteModal from "../components/RoleBasedInviteModal";
import BulkInviteCSVModal from "../components/BulkInviteCSVModal";
import FederationStatusPulse from "../components/FederationStatusPulse";
import AssetProfileEnrichmentBanner from "../components/AssetProfileEnrichmentBanner";
import ApprovalTasksInbox from "../components/ApprovalTasksInbox";
import MyTasksWidget from "../components/dashboard/MyTasksWidget";
import {
  Users, FileSearch, AlertTriangle, FileText, ArrowRight, Shield, Clock, Building2, Copy, Check, UserPlus,
  Crown, UserCog, User, GraduationCap, ClipboardCheck, CheckCircle2, Globe, Server, Zap, Award, Sparkles,
  RefreshCw, Lightbulb, ChevronRight, CircleDot, Circle, FileCheck, Workflow, Bell,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Role Badge Map ── */
const getRoleBadge = (role) => {
  const map = {
    admin: { label: 'Website Admin', icon: Crown, bgColor: 'bg-gradient-to-r from-sky-700 to-sky-800' },
    bcm_manager: { label: 'BCM Manager', icon: UserCog, bgColor: 'bg-gradient-to-r from-sky-700 to-sky-800' },
    it_drp_manager: { label: 'IT-DRP Manager', icon: Server, bgColor: 'bg-gradient-to-r from-teal-500 to-cyan-500' },
    process_manager: { label: 'Process Manager', icon: User, bgColor: 'bg-gradient-to-r from-emerald-500 to-teal-500' },
    process_contributor: { label: 'Process Contributor', icon: User, bgColor: 'bg-gradient-to-r from-slate-500 to-slate-600' },
    app_manager: { label: 'App Manager', icon: Server, bgColor: 'bg-gradient-to-r from-blue-500 to-sky-600' },
    app_contributor: { label: 'App Contributor', icon: User, bgColor: 'bg-gradient-to-r from-slate-500 to-slate-600' },
    it_function_manager: { label: 'IT Function Manager', icon: Server, bgColor: 'bg-gradient-to-r from-violet-500 to-purple-600' },
    it_function_contributor: { label: 'IT Function Contributor', icon: User, bgColor: 'bg-gradient-to-r from-slate-500 to-slate-600' },
  };
  return map[role] || { label: role?.replace(/_/g, ' ') || 'User', icon: User, bgColor: 'bg-slate-500' };
};

/* ═══ Shared Sub-Components ═══ */
const QuickActionCard = ({ variant, label, text, testId }) => {
  const styles = {
    red: "border-red-200/60 bg-red-50 text-red-600",
    amber: "border-amber-200/60 bg-amber-50 text-amber-600",
    orange: "border-sky-200/60 bg-sky-50 text-sky-700",
  };
  return (
    <div className={`p-3 rounded-lg border ${styles[variant]}`} data-testid={testId}>
      <div className="text-[0.5625rem] font-bold uppercase tracking-wider">{label}</div>
      <div className="text-xs text-slate-500 mt-1 font-medium">{text}</div>
    </div>
  );
};

const ResilienceShield = ({ score, subtitle }) => {
  const color = score >= 80 ? "text-sky-700 border-sky-700" : score >= 50 ? "text-amber-500 border-amber-500" : "text-red-500 border-red-500";
  const glow = score >= 80 ? "shadow-[0_0_0_8px_rgba(11,110,153,0.12)]" : score >= 50 ? "shadow-[0_0_0_8px_rgba(245,158,11,0.12)]" : "shadow-[0_0_0_8px_rgba(239,68,68,0.1)]";
  return (
    <div className="flex flex-col items-center" data-testid="resilience-shield">
      <div className={`w-[110px] h-[110px] rounded-full border-4 ${color} ${glow} flex items-center justify-center mb-2.5`}>
        <span className={`text-4xl font-extrabold ${color.split(' ')[0]}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{score}</span>
      </div>
      <p className="text-[0.6875rem] text-slate-500 text-center">{subtitle}</p>
    </div>
  );
};

const PDCAProgress = ({ plan = 0, doVal = 0, check = 0, act = 0 }) => {
  const items = [
    { label: "PLAN", pct: plan, color: "bg-blue-500", textColor: "text-blue-500" },
    { label: "DO", pct: doVal, color: "bg-amber-500", textColor: "text-amber-500" },
    { label: "CHECK", pct: check, color: "bg-emerald-500", textColor: "text-emerald-500" },
    { label: "ACT", pct: act, color: "bg-red-500", textColor: "text-red-500" },
  ];
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="pdca-progress">
      {items.map((it) => (
        <div key={it.label} className="p-2.5 border border-slate-200 rounded-lg">
          <div className={`text-[0.6875rem] font-bold ${it.textColor}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{it.label}</div>
          <div className="h-[5px] bg-slate-100 mt-1.5 rounded-full">
            <div className={`h-full rounded-full ${it.color}`} style={{ width: `${it.pct}%` }} />
          </div>
          <div className="text-[0.6875rem] text-slate-400 mt-1 font-semibold">{it.pct}%</div>
        </div>
      ))}
    </div>
  );
};

const RiskHeatmap = () => {
  const cells = [
    [1,2,3,4,5],[2,4,6,8,10],[3,6,9,12,15],[4,8,12,16,20],[5,10,15,20,25]
  ];
  const heatColor = (v) => v <= 3 ? "bg-emerald-500/12 text-emerald-600" : v <= 6 ? "bg-amber-500/12 text-amber-600" : v <= 10 ? "bg-rose-500/15 text-rose-600" : "bg-red-500/15 text-red-600";
  return (
    <div data-testid="risk-heatmap">
      <div className="grid grid-cols-[20px_1fr] gap-1.5 items-center">
        <div className="text-[0.5625rem] text-slate-400 font-semibold" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)", textAlign: "center" }}>IMPACT</div>
        <div className="grid grid-cols-5 gap-1">
          {cells.map((row, ri) => row.map((v, ci) => (
            <div key={`${ri}-${ci}`} className={`aspect-square flex items-center justify-center text-[0.6875rem] font-bold rounded-md ${heatColor(v)}`}>{v}</div>
          )))}
        </div>
      </div>
      <p className="text-[0.5625rem] text-slate-400 text-center mt-1 font-semibold">LIKELIHOOD &rarr;</p>
    </div>
  );
};

const TaskDot = ({ color }) => {
  const map = { green: "bg-emerald-500", orange: "bg-sky-600", amber: "bg-amber-500", red: "bg-red-500" };
  return <span className={`w-2 h-2 rounded-full flex-shrink-0 ${map[color] || map.orange}`} />;
};

/* ═══ MAIN DASHBOARD ═══ */
export default function DashboardPage() {
  const { api, user } = useAuth();
  const { completeChecklistStep } = useTrial();
  const [stats, setStats] = useState(null);
  const [kpiSummary, setKpiSummary] = useState(null);
  const [platformStats, setPlatformStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [codeCopied, setCodeCopied] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [biaProgress, setBiaProgress] = useState(null);
  const [biaProgressLoading, setBiaProgressLoading] = useState(false);
  const [userCounter, setUserCounter] = useState(null);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showBulkInvite, setShowBulkInvite] = useState(false);

  // Roles allowed to invite via the unified hierarchical modal
  const canInviteHierarchically = [
    "admin", "bcm_manager", "department_head", "process_manager",
    "it_drp_manager", "it_function_manager", "app_manager",
  ].includes(user?.role);

  useEffect(() => { fetchStats(); }, [user?.role]);
  useEffect(() => {
    if (user?.role === 'bcm_manager' && !aiSummary) { fetchAiSummary(); fetchUserCounter(); }
    if (!['admin', 'bcm_manager'].includes(user?.role) && !biaProgress) { fetchBiaProgress(); }
    // Auto-complete "reviewed_dashboard" checklist step
    completeChecklistStep("reviewed_dashboard");
  }, [user?.role]);

  const fetchUserCounter = async () => { try { const res = await api.get("/organizations/user-counter"); setUserCounter(res.data); } catch {} };
  const handleBackfillIds = async () => {
    setBackfillLoading(true); setBackfillResult(null);
    try {
      const res = await api.post("/organizations/backfill-all-ids");
      setBackfillResult(res.data);
      res.data.total_updated > 0 ? toast.success(`Assigned IDs to ${res.data.total_updated} entities`) : toast.info("All entities already have IDs assigned");
    } catch { toast.error("Failed to backfill IDs"); } finally { setBackfillLoading(false); }
  };
  const fetchBiaProgress = async () => {
    setBiaProgressLoading(true);
    try { const r = await api.get("/bia/my-bia-progress"); if (r.data?.success) setBiaProgress(r.data.data); } catch {} finally { setBiaProgressLoading(false); }
  };
  const fetchAiSummary = async () => {
    setAiSummaryLoading(true);
    try { const r = await api.post("/ai-assistant/dashboard-summary", { org_name: user?.org_name || "Your Organization", user_role: user?.role || "bcm_manager" }); if (r.data?.success) setAiSummary(r.data.data); } catch {} finally { setAiSummaryLoading(false); }
  };
  const fetchStats = async () => {
    try {
      api.get("/kpi/summary").then(r => setKpiSummary(r.data)).catch(() => setKpiSummary(null));
      if (user?.role === 'admin') {
        const [dashRes, platformRes] = await Promise.all([api.get("/bcm-admin/dashboard/stats"), api.get("/admin/platform-stats").catch(() => ({ data: null }))]);
        setStats(dashRes.data); setPlatformStats(platformRes.data || { total_organizations: 1, total_users: dashRes.data?.total_users || 1, active_sessions: 1, total_bia_assessments: dashRes.data?.total_bia_assessments || 0, total_risk_assessments: dashRes.data?.total_risk_assessments || 0, total_plans: dashRes.data?.plans_generated || 0 });
      } else if (user?.role === 'bcm_manager') { const r = await api.get("/bcm-admin/dashboard/stats"); setStats(r.data); }
      else {
        const [dashRes, trainingRes] = await Promise.all([api.get("/bcm-admin/dashboard/stats").catch(() => ({ data: {} })), api.get("/bia-engine/training/assignments").catch(() => ({ data: { assignments: [] } }))]);
        setStats(dashRes.data);
        setUserStats({ my_training: trainingRes.data?.assignments || [], training_completed: trainingRes.data?.assignments?.filter(a => a.status === 'completed').length || 0, training_pending: trainingRes.data?.assignments?.filter(a => a.status !== 'completed').length || 0 });
      }
    } catch { setStats({ total_users: 0, total_bia_assessments: 0, total_risk_assessments: 0, high_risks: 0, plans_generated: 0, avg_risk_score: 0 }); }
    finally { setLoading(false); }
  };
  const copyOrgCode = async () => {
    if (user?.org_code) { try { await navigator.clipboard.writeText(user.org_code); setCodeCopied(true); toast.success("Organization code copied!"); setTimeout(() => setCodeCopied(false), 2000); } catch { toast.error("Failed to copy"); } }
  };

  if (loading) return <div className="flex items-center justify-center h-64" data-testid="dashboard-loading"><div className="w-10 h-10 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" /></div>;

  // Computed values from stats (support both nested and flat formats)
  const biaCount = stats?.bia?.combined_total ?? stats?.bia?.total ?? stats?.total_bia_assessments ?? 0;
  const biaApproved = stats?.bia?.approved ?? 0;
  const biaCompletionPct = stats?.bia?.completion_pct ?? 0;
  const riskCount = stats?.risks?.total || stats?.total_risk_assessments || 0;
  const highRisks = stats?.risks?.high_priority || stats?.high_risks || 0;
  const plansCount = stats?.plans?.combined_total ?? stats?.plans?.total ?? stats?.plans_generated ?? 0;
  const teamCount = stats?.users?.total || stats?.total_users || 0;

  // Organization Health = the SINGLE overall score (GET /api/kpi/summary
  // overall_score) shared with the P&C headline and BCM Report. Grade/standing/
  // level derived from that score. null → "Not yet scored".
  const _kpiCats = kpiSummary?.categories;
  const overallScore = (kpiSummary && kpiSummary.overall_score != null) ? Math.round(kpiSummary.overall_score) : null;
  const kpiMeasured = _kpiCats ? Object.values(_kpiCats).reduce((a, c) => a + (c.measured_count || 0), 0) : 0;
  const kpiTotal = _kpiCats ? Object.values(_kpiCats).reduce((a, c) => a + (c.kpi_count || 0), 0) : 15;
  const hasResilienceData = overallScore !== null;
  const resilienceScore = overallScore ?? 0;

  /* ════════ WEBSITE ADMIN ════════ */
  if (user?.role === 'admin') {
    return (
      <div className="space-y-5 animate-fade-in" data-testid="dashboard-page">
        <GuidedTour />
        <DemoMaturityBanner user={user} />
        <AssetProfileEnrichmentBanner />
        <ApprovalTasksInbox />
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-slate-800" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>BCM Command Center</h1>
            <p className="text-[0.6875rem] text-slate-400">ISO 22301:2019 &mdash; Platform Administration</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center border border-slate-200 rounded-lg text-slate-500 text-[0.6875rem] relative cursor-pointer"><Bell className="w-4 h-4" /><span className="absolute top-1 right-1 w-[7px] h-[7px] rounded-full bg-red-500 border-[1.5px] border-white" /></div>
            <div className="w-8 h-8 rounded-full bg-sky-50 border-2 border-sky-700 flex items-center justify-center text-[0.625rem] font-bold text-sky-700">AD</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-3 gap-2.5">
          <QuickActionCard variant="red" label="System" text={`${platformStats?.total_organizations || 1} organization(s) active`} testId="qa-system" />
          <QuickActionCard variant="amber" label="Users" text={`${platformStats?.total_users || 0} total platform users`} testId="qa-users" />
          <QuickActionCard variant="orange" label="Health" text="All systems operational" testId="qa-health" />
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "Organizations", val: platformStats?.total_organizations || 1, icon: Globe, color: "text-sky-700" },
            { label: "Total Users", val: platformStats?.total_users || 0, icon: Users, color: "text-amber-500" },
            { label: "Active Sessions", val: platformStats?.active_sessions || 1, icon: Zap, color: "text-emerald-500" },
            { label: "System Health", val: "100%", icon: Server, color: "text-blue-500" },
          ].map((m) => (
            <div key={m.label} className="bg-white border border-slate-200 rounded-lg p-4 hover:-translate-y-0.5 hover:shadow-sm transition-all" data-testid={`stat-${m.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[0.5625rem] font-bold text-slate-400 uppercase tracking-wider">{m.label}</p>
                  <p className="text-2xl font-extrabold text-slate-800 mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{m.val}</p>
                </div>
                <m.icon className={`w-8 h-8 ${m.color} opacity-60`} />
              </div>
            </div>
          ))}
        </div>

        {/* Activity + Actions */}
        <div className="grid grid-cols-4 gap-3">
          <div className="col-span-2 bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="text-[0.6875rem] font-bold text-slate-500 uppercase tracking-wider mb-3">Platform Activity</h3>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "BIA Assessments", val: platformStats?.total_bia_assessments || 0, color: "text-blue-600", bg: "bg-blue-50" },
                { label: "Risk Assessments", val: platformStats?.total_risk_assessments || 0, color: "text-amber-600", bg: "bg-amber-50" },
                { label: "Plans Generated", val: platformStats?.total_plans || 0, color: "text-emerald-600", bg: "bg-emerald-50" },
              ].map((s) => (
                <div key={s.label} className={`p-3 rounded-lg ${s.bg} text-center`}>
                  <p className={`text-xl font-bold ${s.color}`}>{s.val}</p>
                  <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-2 bg-white border border-slate-200 rounded-lg p-5">
            <h3 className="text-[0.6875rem] font-bold text-slate-500 uppercase tracking-wider mb-3">Admin Quick Actions</h3>
            <div className="space-y-2">
              {[
                { to: "/admin", label: "Admin Panel", sub: "Manage platform settings", icon: Shield, color: "text-sky-700" },
                { to: "/org-management", label: "Organizations", sub: "View all organizations", icon: Building2, color: "text-sky-700" },
                { to: "/compliance-report", label: "Compliance Report", sub: "Platform compliance", icon: Award, color: "text-blue-500" },
              ].map((a) => (
                <Link key={a.to} to={a.to} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 hover:bg-sky-50 hover:border-sky-200 transition-colors">
                  <a.icon className={`w-4 h-4 ${a.color}`} />
                  <div><p className="text-xs font-semibold text-slate-800">{a.label}</p><p className="text-[10px] text-slate-400">{a.sub}</p></div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ════════ BCM MANAGER — MISSION CONTROL ════════ */
  if (user?.role === 'bcm_manager') {
    const planPct = biaCount > 0 ? Math.min(100, 50 + biaCount * 7) : 20;
    const doPct = plansCount > 0 ? Math.min(100, 30 + plansCount * 10) : 15;
    const checkPct = riskCount > 0 ? Math.min(100, 20 + riskCount * 5) : 10;
    const actPct = Math.min(100, 10 + plansCount * 3);
    const healthGrade = resilienceScore >= 90 ? 'A' : resilienceScore >= 75 ? 'B' : resilienceScore >= 60 ? 'C' : resilienceScore >= 40 ? 'D' : 'F';
    const gradeColor = { A: 'text-teal-600 border-teal-500', B: 'text-sky-700 border-sky-600', C: 'text-amber-500 border-amber-400', D: 'text-rose-500 border-rose-400', F: 'text-red-600 border-red-500' }[healthGrade];

    return (
      <div className="space-y-5 animate-fade-in" data-testid="dashboard-page">
        <GuidedTour />
        <DemoMaturityBanner user={user} />
        <AssetProfileEnrichmentBanner />
        <ApprovalTasksInbox />
        <FederationStatusPulse />

        <MyTasksWidget />

        {/* ── Page Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Mission Control</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {user?.first_name || 'BCM Admin'}.
              {highRisks > 0 && <> You have <span className="font-semibold text-rose-500">{highRisks} high-risk</span> item{highRisks > 1 ? 's' : ''}.</>}
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            {canInviteHierarchically && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5"
                data-testid="dashboard-invite-team-btn"
              >
                <UserPlus className="w-3.5 h-3.5" /> Invite Team Member
              </button>
            )}
            {canInviteHierarchically && (
              <button
                onClick={() => setShowBulkInvite(true)}
                className="px-3.5 py-2 border border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5"
                data-testid="dashboard-bulk-invite-btn"
              >
                <UserPlus className="w-3.5 h-3.5" /> Bulk CSV
              </button>
            )}
            <Link to="/reports"><button className="px-3.5 py-2 border border-slate-200 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-50 transition-colors" data-testid="view-reports-btn">View Reports</button></Link>
            <Link to="/operational-bia"><button className="px-3.5 py-2 bg-sky-700 hover:bg-sky-800 text-white rounded-lg text-xs font-semibold transition-colors" data-testid="new-assessment-btn">+ New Assessment</button></Link>
          </div>
        </div>

        {/* ── KPI Strip ── */}
        <div className="grid grid-cols-4 gap-4" data-testid="kpi-strip">
          {[
            { label: 'BIA Completion', val: biaCount > 0 ? `${biaCompletionPct}%` : '0%', icon: FileSearch, accent: 'bg-sky-700', iconBg: 'bg-sky-100 text-sky-700', trend: biaCount > 0 ? `${biaApproved} of ${biaCount} approved` : 'No assessments yet', barPct: biaCount > 0 ? biaCompletionPct : 0, barColor: 'bg-sky-700' },
            { label: 'Team Members', val: teamCount, icon: Users, accent: 'bg-teal-500', iconBg: 'bg-teal-100 text-teal-700', trend: `${teamCount} active user${teamCount !== 1 ? 's' : ''}`, barPct: Math.min(100, teamCount * 10), barColor: 'bg-teal-500' },
            { label: 'Risk Assessments', val: riskCount, icon: AlertTriangle, accent: 'bg-amber-500', iconBg: 'bg-amber-100 text-amber-700', trend: highRisks > 0 ? `${highRisks} high-risk items` : 'All risks managed', barPct: Math.min(100, riskCount * 12), barColor: 'bg-amber-500' },
            { label: 'Recovery Plans', val: plansCount, icon: FileText, accent: 'bg-purple-600', iconBg: 'bg-purple-100 text-purple-700', trend: plansCount > 0 ? `${plansCount} plan(s) generated` : 'No plans yet', barPct: Math.min(100, plansCount * 20), barColor: 'bg-purple-600' },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm relative overflow-hidden" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className={`absolute top-0 left-0 right-0 h-[3px] ${kpi.accent}`} />
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${kpi.iconBg}`}>
                <kpi.icon className="w-4 h-4" />
              </div>
              <p className="text-[0.7rem] font-semibold text-slate-500 uppercase tracking-wider">{kpi.label}</p>
              <p className="text-2xl font-extrabold text-slate-800 mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{kpi.val}</p>
              <div className="h-1 bg-slate-100 rounded-full mt-3 overflow-hidden">
                <div className={`h-full rounded-full ${kpi.barColor} transition-all duration-700`} style={{ width: `${kpi.barPct}%` }} />
              </div>
              <p className="text-xs text-slate-500 mt-2 font-medium">{kpi.trend}</p>
            </div>
          ))}
        </div>

        {/* ── Quick Actions ── */}
        <div className="flex gap-3 flex-wrap" data-testid="quick-actions-row">
          {[
            { to: '/operational-bia', label: 'Operational BIAs', count: biaCount || null },
            { to: '/recovery-plans', label: 'Generate Recovery Plans', ai: true },
            { to: '/exercises', label: 'Schedule Exercise' },
            { to: '/management-review', label: 'Start Management Review' },
          ].map((qa) => (
            <Link key={qa.to} to={qa.to}>
              <button className="px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:border-sky-600 hover:text-sky-700 hover:bg-sky-50 transition-all flex items-center gap-2" data-testid={`qa-${qa.label.toLowerCase().replace(/\s/g, '-')}`}>
                {qa.ai && <span className="text-purple-600 font-bold text-xs">AI</span>}
                {qa.label}
                {qa.count !== null && qa.count > 0 && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-600">{qa.count}</span>}
              </button>
            </Link>
          ))}
        </div>

        {/* ── V14: Decentralized Pending Approvals (read-only for BCM) ── */}
        <PendingApprovalsWidget />

        {/* ── BCM Health Dashboard ── */}
        <BCMHealthDashboard />

        {/* ── 3-Column Mission Control Grid ── */}
        <div className="grid grid-cols-[260px_1fr_280px] gap-5" data-testid="mission-control-grid">

          {/* ═══ LEFT COLUMN: Health Score ═══ */}
          <div className="space-y-4">
            {/* Health Ring */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm text-center" data-testid="health-score-card">
              <p className="text-[0.7rem] font-semibold text-slate-500 uppercase tracking-wider">Organization Health</p>
              {hasResilienceData ? (
                <>
                  <div className={`w-[130px] h-[130px] rounded-full border-[10px] ${gradeColor} mx-auto mt-4 mb-2 flex items-center justify-center relative`}>
                    <span className="text-[2.2rem] font-extrabold text-slate-800" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{resilienceScore}</span>
                    <span className={`absolute -bottom-1 px-3 py-0.5 rounded-full text-[0.7rem] font-bold bg-white border-2 ${gradeColor}`}>Grade {healthGrade}</span>
                  </div>
                  <p className="text-sm font-bold text-slate-800 mt-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {resilienceScore >= 75 ? 'Good Standing' : resilienceScore >= 50 ? 'Needs Improvement' : 'At Risk'}
                  </p>
                  <div className="inline-flex items-center gap-1.5 bg-sky-50 text-sky-700 px-3 py-1.5 rounded-full text-xs font-semibold mt-3">
                    <Shield className="w-3.5 h-3.5" />
                    Level {resilienceScore >= 80 ? 4 : resilienceScore >= 60 ? 3 : resilienceScore >= 40 ? 2 : 1}: {resilienceScore >= 80 ? 'Measured' : resilienceScore >= 60 ? 'Defined' : resilienceScore >= 40 ? 'Managed' : 'Initial'}
                  </div>
                  <p className="text-[0.7rem] text-slate-400 mt-2" data-testid="health-measured-coverage">Based on {kpiMeasured} of {kpiTotal} measured indicators</p>
                </>
              ) : (
                <>
                  <div className="w-[130px] h-[130px] rounded-full border-[10px] border-slate-200 mx-auto mt-4 mb-2 flex items-center justify-center" data-testid="resilience-not-scored">
                    <span className="text-sm font-bold text-slate-400 text-center px-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Not yet scored</span>
                  </div>
                  <p className="text-sm font-bold text-slate-500 mt-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    No data yet
                  </p>
                  <div className="inline-flex items-center gap-1.5 bg-slate-50 text-slate-500 px-3 py-1.5 rounded-full text-xs font-semibold mt-3">
                    <Shield className="w-3.5 h-3.5" />
                    Add a BIA, plan or risk to begin
                  </div>
                </>
              )}

              {/* Quick Stats */}
              <div className="mt-5 space-y-2.5 text-left">
                {[
                  { label: 'Active Users', val: teamCount },
                  { label: 'BIA Assessments', val: biaCount },
                  { label: 'High Risks', val: highRisks, warn: highRisks > 0 },
                  { label: 'Recovery Plans', val: plansCount },
                ].map((qs) => (
                  <div key={qs.label} className="flex items-center justify-between px-3.5 py-2.5 bg-slate-50 rounded-lg">
                    <span className="text-xs text-slate-500">{qs.label}</span>
                    <span className={`text-sm font-bold ${qs.warn ? 'text-rose-500' : 'text-slate-800'}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{qs.val}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Org Code Mini */}
            {user?.org_code && (
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="org-code-card">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-sky-700 flex items-center justify-center"><Building2 className="w-4 h-4 text-white" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-sky-700 uppercase tracking-wider">Org Code</p>
                    <p className="text-base font-bold tracking-widest text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{user.org_code}</p>
                  </div>
                  <button onClick={copyOrgCode} className="p-2 border border-sky-200 text-sky-700 rounded-lg hover:bg-sky-50 transition-colors" data-testid="copy-org-code-btn">
                    {codeCopied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            )}

            {/* User License Mini */}
            <div className={`bg-white border rounded-xl p-4 shadow-sm ${userCounter?.is_trial && userCounter?.remaining_slots <= 2 ? 'border-amber-200' : 'border-slate-200'}`} data-testid="user-counter-card">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${userCounter?.is_trial && userCounter?.remaining_slots <= 2 ? 'bg-amber-500' : 'bg-emerald-500'}`}><Users className="w-4 h-4 text-white" /></div>
                <div className="flex-1">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">User Licenses</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-base font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{userCounter?.total_users || 0}</span>
                    {userCounter?.user_limit && <span className="text-xs text-slate-400">/ {userCounter.user_limit}</span>}
                  </div>
                </div>
                {userCounter?.is_trial && <Badge className="bg-amber-100 text-amber-700 text-[9px]">Trial</Badge>}
              </div>
              {userCounter?.user_limit && (
                <div className="mt-2"><div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className={`h-full rounded-full transition-all ${userCounter.remaining_slots <= 2 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, (userCounter.total_users / userCounter.user_limit) * 100)}%` }} /></div></div>
              )}
            </div>
          </div>

          {/* ═══ CENTER COLUMN: Task Queue + PDCA ═══ */}
          <div className="space-y-4 min-w-0">
            {/* Task Queue */}
            <BCMProgramAttention />

            {/* PDCA Phase Progress — Horizontal Bars */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="pdca-bars-card">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
                <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>PDCA Phase Progress</h3>
              </div>
              <div className="px-5 py-4 space-y-3">
                {[
                  { label: 'PLAN', pct: planPct, color: 'bg-blue-500', text: 'text-blue-600' },
                  { label: 'DO', pct: doPct, color: 'bg-teal-500', text: 'text-teal-600' },
                  { label: 'CHECK', pct: checkPct, color: 'bg-amber-500', text: 'text-amber-600' },
                  { label: 'ACT', pct: actPct, color: 'bg-purple-600', text: 'text-purple-600' },
                ].map((phase) => (
                  <div key={phase.label} className="flex items-center gap-3">
                    <span className={`w-14 text-xs font-bold tracking-wide ${phase.text}`}>{phase.label}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${phase.color} transition-all duration-700`} style={{ width: `${phase.pct}%` }} />
                    </div>
                    <span className="text-xs font-semibold text-slate-600 w-9 text-right">{phase.pct}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Data Maintenance */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="backfill-ids-card">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-700 to-teal-600 flex items-center justify-center shadow-sm"><Zap className="w-4 h-4 text-white" /></div>
                  <div><h3 className="text-xs font-semibold text-slate-800">Data Maintenance</h3><p className="text-[10px] text-slate-400">Assign auto-generated IDs to entities</p></div>
                </div>
                <button onClick={handleBackfillIds} disabled={backfillLoading} className="px-3 py-1.5 bg-sky-700 hover:bg-sky-800 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 disabled:opacity-60 transition-colors" data-testid="backfill-ids-btn">
                  {backfillLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                  {backfillLoading ? 'Running...' : 'Backfill All IDs'}
                </button>
              </div>
              {backfillResult && (
                <div className="mt-3 grid grid-cols-4 gap-2" data-testid="backfill-results">
                  {[{ label: 'Depts', key: 'departments', prefix: 'DEPT' }, { label: 'Processes', key: 'processes', prefix: 'BP' }, { label: 'Apps', key: 'applications', prefix: 'APP' }, { label: 'IT Funcs', key: 'it_functions', prefix: 'ITF' }].map(({ label, key, prefix }) => (
                    <div key={key} className={`text-center p-2 rounded-lg ${backfillResult.details[key] > 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-200'}`}>
                      <p className={`text-lg font-bold ${backfillResult.details[key] > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>{backfillResult.details[key]}</p>
                      <p className="text-[10px] text-slate-500">{label}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ═══ RIGHT COLUMN: Activity + AI + Deadlines ═══ */}
          <div className="space-y-4">
            {/* Team Activity (compact) */}
            <TeamActivityPanel />

            {/* AI Insights */}
            <div className="bg-white border border-purple-200 rounded-xl shadow-sm overflow-hidden" data-testid="ai-summary-card">
              <div className="flex items-center justify-between px-4 py-3 border-b border-purple-100 bg-purple-50/50">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <h3 className="text-xs font-bold text-slate-700">AI Insights</h3>
                  {aiSummary?.health_score && <Badge className={aiSummary.health_status === 'healthy' ? 'bg-emerald-500 text-white' : 'bg-amber-100 text-amber-700'} style={{ fontSize: '9px' }}>{aiSummary.health_score}%</Badge>}
                </div>
                <button onClick={fetchAiSummary} disabled={aiSummaryLoading} className="text-purple-600 hover:text-purple-700 p-0.5"><RefreshCw className={`w-3.5 h-3.5 ${aiSummaryLoading ? 'animate-spin' : ''}`} /></button>
              </div>
              <div className="px-4 py-3">
                {aiSummaryLoading && !aiSummary ? (
                  <div className="flex items-center justify-center py-4"><div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>
                ) : aiSummary ? (
                  <div className="space-y-2.5">
                    <p className="text-xs text-slate-600 leading-relaxed">{aiSummary.summary}</p>
                    {aiSummary.attention_items?.length > 0 && aiSummary.attention_items.slice(0, 2).map((a, i) => (
                      <div key={i} className="flex gap-2 text-[11px]">
                        <span className="text-amber-500 font-bold mt-px">!</span>
                        <span className="text-slate-600">{a}</span>
                      </div>
                    ))}
                    {aiSummary.recommendation && (
                      <div className="flex gap-2 p-2.5 bg-purple-50 rounded-lg text-[11px]">
                        <Lightbulb className="w-3.5 h-3.5 text-purple-600 flex-shrink-0 mt-0.5" />
                        <span className="text-purple-700">{aiSummary.recommendation}</span>
                      </div>
                    )}
                  </div>
                ) : <p className="text-center py-3 text-slate-400 text-xs">Click refresh to load AI insights</p>}
              </div>
            </div>

            {/* Upcoming Deadlines / Recent Activity */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="deadlines-card">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h3 className="text-xs font-bold text-slate-700">Recent Activity</h3>
              </div>
              <div className="divide-y divide-slate-100">
                {[
                  { color: highRisks > 0 ? 'bg-red-500' : 'bg-teal-500', text: highRisks > 0 ? `${highRisks} high-risk item(s) need attention` : 'No overdue items', time: highRisks > 0 ? 'Action needed' : 'All clear' },
                  { color: 'bg-teal-500', text: `${plansCount} Recovery Plan(s) generated`, time: 'Active' },
                  { color: 'bg-sky-600', text: `${biaCount} BIA assessment(s) completed`, time: 'Active' },
                  { color: 'bg-amber-500', text: `${riskCount} Risk assessment(s) on file`, time: 'Active' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5 px-4 py-2.5">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${item.color}`} />
                    <span className="flex-1 text-xs text-slate-600">{item.text}</span>
                    <span className="text-[10px] text-slate-400 whitespace-nowrap">{item.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <AIInsightsPanel />
        <RoleBasedInviteModal
          open={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onInvited={() => fetchStats()}
        />
        <BulkInviteCSVModal
          open={showBulkInvite}
          onClose={() => setShowBulkInvite(false)}
          onInvited={() => fetchStats()}
        />
      </div>
    );
  }

  /* ════════ END USER / CONTRIBUTOR / MANAGER ════════ */
  const roleBadge = getRoleBadge(user?.role);
  const RoleIcon = roleBadge.icon;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="dashboard-page">
      <GuidedTour />
      <DemoMaturityBanner user={user} />

      {/* Header Bar */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-slate-800" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {['process_manager', 'app_manager', 'it_function_manager'].includes(user?.role) ? 'My BCM Workspace' : 'My Tasks'}
          </h1>
          <p className="text-[0.6875rem] text-slate-400">{roleBadge.label} &mdash; {user?.org_name || 'Organization'}</p>
        </div>
        <div className="flex items-center gap-2.5">
          {canInviteHierarchically && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[11px] font-semibold transition-colors flex items-center gap-1.5"
              data-testid="dashboard-invite-team-btn-2"
            >
              <UserPlus className="w-3.5 h-3.5" /> Invite
            </button>
          )}
          <div className="w-8 h-8 rounded-full bg-sky-50 border-2 border-sky-700 flex items-center justify-center text-[0.625rem] font-bold text-sky-700" data-testid="user-avatar">
            {(user?.first_name?.[0] || 'U') + (user?.last_name?.[0] || '')}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-3 gap-2.5">
        <QuickActionCard variant="red" label="Action Required" text={biaProgress?.summary?.draft > 0 ? `${biaProgress.summary.draft} BIA(s) in draft` : "No overdue items"} testId="qa-action" />
        <QuickActionCard variant="amber" label="Pending" text={userStats?.training_pending > 0 ? `${userStats.training_pending} training module(s) pending` : "No pending tasks"} testId="qa-pending" />
        <QuickActionCard variant="orange" label="Compliance" text="Review policies for certification" testId="qa-compliance" />
      </div>

      {/* My Tasks — guided role-based progression */}
      <MyTasksWidget />


      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-lg p-4 hover:-translate-y-0.5 hover:shadow-sm transition-all">
          <div className="flex items-center justify-between">
            <div><p className="text-[0.5625rem] font-bold text-slate-400 uppercase tracking-wider">Training Done</p><p className="text-2xl font-extrabold text-emerald-600 mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{userStats?.training_completed || 0}</p></div>
            <CheckCircle2 className="w-8 h-8 text-emerald-400 opacity-60" />
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4 hover:-translate-y-0.5 hover:shadow-sm transition-all">
          <div className="flex items-center justify-between">
            <div><p className="text-[0.5625rem] font-bold text-slate-400 uppercase tracking-wider">Training Pending</p><p className="text-2xl font-extrabold text-amber-600 mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{userStats?.training_pending || 0}</p></div>
            <Clock className="w-8 h-8 text-amber-400 opacity-60" />
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4 hover:-translate-y-0.5 hover:shadow-sm transition-all">
          <div className="flex items-center justify-between">
            <div><p className="text-[0.5625rem] font-bold text-slate-400 uppercase tracking-wider">My BIAs</p><p className="text-2xl font-extrabold text-blue-600 mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{biaProgress?.summary?.total || 0}</p></div>
            <FileSearch className="w-8 h-8 text-blue-400 opacity-60" />
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4 hover:-translate-y-0.5 hover:shadow-sm transition-all">
          <div className="flex items-center justify-between">
            <div><p className="text-[0.5625rem] font-bold text-slate-400 uppercase tracking-wider">Exercises</p><p className="text-2xl font-extrabold text-sky-600 mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>--</p></div>
            <ClipboardCheck className="w-8 h-8 text-sky-400 opacity-60" />
          </div>
        </div>
      </div>

      {/* BIA Progress Tracker */}
      <div className="bg-white border border-blue-200 rounded-lg p-5 shadow-sm" data-testid="bia-progress-tracker">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-600 to-teal-500 flex items-center justify-center shadow-sm"><Workflow className="w-4 h-4 text-white" /></div>
            <div><h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>BIA Progress Tracker</h3><p className="text-[10px] text-slate-400">Track your Business Impact Assessments</p></div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchBiaProgress} disabled={biaProgressLoading} className="text-blue-600 hover:text-blue-700 p-1"><RefreshCw className={`w-4 h-4 ${biaProgressLoading ? 'animate-spin' : ''}`} /></button>
            <Link to="/operational-bia"><button className="px-3 py-1.5 border border-blue-200 text-blue-600 rounded-lg text-xs font-medium hover:bg-blue-50 transition-colors flex items-center gap-1"><FileSearch className="w-3.5 h-3.5" />Create BIA</button></Link>
          </div>
        </div>
        {biaProgressLoading && !biaProgress ? (
          <div className="flex items-center justify-center py-6"><div className="flex items-center gap-3 text-blue-600"><div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /><span className="text-xs">Loading BIA progress...</span></div></div>
        ) : biaProgress?.bias?.length > 0 ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3 p-2.5 bg-slate-50 rounded-lg text-[10px]">
              <div className="flex items-center gap-1.5"><Circle className="w-3 h-3 text-slate-400" /><span className="text-slate-600">Draft</span></div>
              <div className="flex items-center gap-1.5"><CircleDot className="w-3 h-3 text-amber-500" /><span className="text-slate-600">Submitted</span></div>
              <div className="flex items-center gap-1.5"><Clock className="w-3 h-3 text-blue-500" /><span className="text-slate-600">Under Review</span></div>
              <div className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-500" /><span className="text-slate-600">Approved</span></div>
              <div className="flex items-center gap-1.5"><FileCheck className="w-3 h-3 text-purple-500" /><span className="text-slate-600">Tasks Created</span></div>
            </div>
            <div className="space-y-2">
              {biaProgress.bias.slice(0, 5).map((bia) => (
                <div key={bia.id} className="p-3 border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between mb-1.5">
                    <div><p className="font-medium text-slate-800 text-xs">{bia.process_name}</p><p className="text-[10px] text-slate-400">{bia.business_unit} &bull; RTO: {bia.rto || 'N/A'}h &bull; RPO: {bia.rpo || 'N/A'}h</p></div>
                    <Badge className={`text-[10px] ${bia.stage === 'draft' ? 'bg-slate-100 text-slate-700' : bia.stage === 'submitted' ? 'bg-amber-100 text-amber-700' : bia.stage === 'under_review' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>{bia.stage.replace(/_/g, ' ')}</Badge>
                  </div>
                  <div className="flex items-center gap-1">{[1,2,3,4,5].map((step) => (<div key={step} className="flex-1 flex items-center"><div className={`h-1.5 flex-1 rounded-full ${step <= bia.stage_num ? step <= 2 ? 'bg-amber-400' : step <= 3 ? 'bg-blue-500' : 'bg-emerald-500' : 'bg-slate-200'}`} />{step < 5 && <ChevronRight className={`w-3 h-3 ${step < bia.stage_num ? 'text-slate-400' : 'text-slate-200'}`} />}</div>))}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-5 gap-2 pt-2 border-t border-slate-100">
              {[{ label: "Draft", val: biaProgress.summary.draft, bg: "bg-slate-50", color: "text-slate-600" }, { label: "Submitted", val: biaProgress.summary.submitted, bg: "bg-amber-50", color: "text-amber-600" }, { label: "In Review", val: biaProgress.summary.under_review, bg: "bg-blue-50", color: "text-blue-600" }, { label: "Approved", val: biaProgress.summary.approved, bg: "bg-emerald-50", color: "text-emerald-600" }, { label: "Tasks", val: biaProgress.summary.tasks_created, bg: "bg-purple-50", color: "text-purple-600" }].map((s) => (
                <div key={s.label} className={`text-center p-2 rounded ${s.bg}`}><p className={`text-lg font-bold ${s.color}`}>{s.val}</p><p className="text-[10px] text-slate-500">{s.label}</p></div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-6"><FileSearch className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-400 text-xs mb-1">No BIAs created yet</p><Link to="/operational-bia"><button className="mt-2 px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 mx-auto"><FileSearch className="w-3.5 h-3.5" />Create Your First BIA</button></Link></div>
        )}
      </div>

      {/* Training + Quick Actions */}
      <div className="grid grid-cols-4 gap-3">
        <div className="col-span-2 bg-white border border-slate-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[0.6875rem] font-bold text-slate-500 uppercase tracking-wider">My Training</h3>
            <Link to="/training-awareness" className="text-sky-700 text-[10px] font-medium hover:text-sky-800 flex items-center gap-0.5">View all <ArrowRight className="w-3 h-3" /></Link>
          </div>          {userStats?.my_training?.length > 0 ? (
            <div className="space-y-2">{userStats.my_training.slice(0, 3).map((t, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50"><div className="flex items-center gap-2.5"><GraduationCap className="w-4 h-4 text-teal-500" /><div><p className="font-medium text-slate-800 text-xs">{t.training_level_name || t.training_level}</p><p className="text-[10px] text-slate-400">Due: {t.due_date ? new Date(t.due_date).toLocaleDateString() : 'No deadline'}</p></div></div><Badge variant={t.status === 'completed' ? 'default' : 'secondary'} className="text-[10px]">{t.status}</Badge></div>
            ))}</div>
          ) : <div className="text-center py-6"><GraduationCap className="w-10 h-10 mx-auto mb-2 text-slate-300" /><p className="text-xs text-slate-400">No training assigned yet</p></div>}
        </div>
        <div className="col-span-2 bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="text-[0.6875rem] font-bold text-slate-500 uppercase tracking-wider mb-3">Quick Actions</h3>
          <div className="space-y-2">
            {[
              { to: "/training-awareness", label: "My Training", sub: "Complete assigned training", icon: GraduationCap, color: "text-teal-500" },
              { to: "/operational-bia", label: "BIA Assessments", sub: "View or complete BIAs", icon: FileSearch, color: "text-blue-500" },
              { to: "/exercises", label: "Exercises", sub: "View upcoming exercises", icon: ClipboardCheck, color: "text-sky-600" },
            ].map((a) => (
              <Link key={a.to} to={a.to} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 hover:bg-sky-50 hover:border-sky-200 transition-colors">
                <a.icon className={`w-4 h-4 ${a.color}`} />
                <div><p className="text-xs font-semibold text-slate-800">{a.label}</p><p className="text-[10px] text-slate-400">{a.sub}</p></div>
              </Link>
            ))}
          </div>
        </div>
      </div>
      <RoleBasedInviteModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvited={() => fetchStats()}
      />
      <BulkInviteCSVModal
        open={showBulkInvite}
        onClose={() => setShowBulkInvite(false)}
        onInvited={() => fetchStats()}
      />
    </div>
  );
}
